import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import ColumnVisibilityMenu from 'src/components/ColumnVisibilityMenu';
import Iconify from 'src/components/Iconify';
import ListActionBar from 'src/components/ListActionBar';
import TableActionButton from 'src/components/TableActionButton';
import { useColumnVisibility } from 'src/hooks/columnVisibility';
import { useInfiniteWin32AppWdacFiles, useWin32AppWdacFileCounts } from 'src/hooks/wdac';
import { EXPANDED_ROWS_LIMIT } from 'src/constants/table';
import { formatCount } from 'src/utils/formatNumber.util';
import Win32FileTable, {
  DEFAULT_FILE_SORT,
  DEFAULT_VISIBLE_WIN32_FILE_COLUMNS,
  WIN32_FILE_COLUMNS,
  toIsSigned,
  toggleFileSort,
} from '../Win32FileTable';
import Win32FileDetailModal from '../Win32FileDetailModal';
import { COVERAGE_CARDS, TELEMETRY_FILTER_OPTIONS, withReportedWindow } from './constants';

const IMPORT_DISABLED_REASON = `Filter to "${COVERAGE_CARDS[0].label}" to create a WDAC import. Only files the app doesn't hold yet can be imported.`;

const CoverageCard = styled(Box, {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  flex: 1,
  minWidth: 0,
  cursor: 'pointer',
  borderRadius: 8,
  border: `1px solid ${active ? '#87C8E0' : '#5B5B5B'}`,
  backgroundColor: active ? '#3E4E55' : '#4B4B4B',
  padding: '14px 18px',
}));

const TelemetryFilesTable = ({ globalApplicationVersionId, filters, onFiltersChange }) => {
  const { onEditStatus } = useOutletContext() ?? {};
  const [searchValue, setSearchValue] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [sort, setSort] = useState(DEFAULT_FILE_SORT);
  const [openFileId, setOpenFileId] = useState(null);
  const { visibleColumnDefinitions, isColumnVisible, setColumnVisible, resetColumns } =
    useColumnVisibility(
      'win32AppTelemetryFiles',
      WIN32_FILE_COLUMNS,
      DEFAULT_VISIBLE_WIN32_FILE_COLUMNS,
    );

  // A null isCovered shows every reported file. Only files not yet in the app can be imported,
  // so selection and the import button are live in that view alone.
  const { isCovered } = filters;
  const { reportedWindow } = filters;
  const isSigned = toIsSigned(filters.isSigned);
  const canImport = isCovered === false;
  const trimmedSearch = searchValue.trim();

  // The cards count under the same window and signature as the table, so a card's number is the
  // number of rows it opens.
  const countsQuery = useWin32AppWdacFileCounts({
    globalApplicationVersionId,
    reportedWindow,
    isSigned,
  });
  const filesQuery = useInfiniteWin32AppWdacFiles({
    globalApplicationVersionId,
    isCovered,
    reportedWindow,
    isSigned,
    search: trimmedSearch,
    sortColumn: sort.column,
    sortDesc: sort.direction === 'desc',
    limit: EXPANDED_ROWS_LIMIT,
  });
  const results = useMemo(
    () => filesQuery.data?.pages?.flatMap(page => page.results || []) || [],
    [filesQuery.data],
  );
  const totalCount = filesQuery.data?.pages?.[0]?.totalCount || 0;

  // A selection belongs to the rows it was made over, so it is dropped when they change.
  useEffect(() => {
    setSelected(new Set());
  }, [isCovered, reportedWindow, isSigned, trimmedSearch]);

  const loadMore = useCallback(() => {
    if (filesQuery.hasNextPage) {
      filesQuery.fetchNextPage();
    }
  }, [filesQuery]);

  const [sentryRef] = useInfiniteScroll({
    loading: filesQuery.isFetchingNextPage,
    hasNextPage: !!filesQuery.hasNextPage,
    onLoadMore: loadMore,
    rootMargin: '0px 0px 400px 0px',
  });

  const toggleSelected = fileId =>
    setSelected(previous => {
      const next = new Set(previous);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });

  const onFilterChange = nextFilters =>
    onFiltersChange(
      withReportedWindow({
        reportedWindow: nextFilters.reportedWindow,
        isCovered: typeof nextFilters.isCovered === 'boolean' ? nextFilters.isCovered : null,
        isSigned: nextFilters.isSigned ?? [],
      }),
    );

  const onCoverageCardClick = value =>
    onFiltersChange(previous => ({
      ...previous,
      isCovered: previous.isCovered === value ? null : value,
    }));

  // The import is made by trusting the app with it, so it opens the status form scoped to what this
  // table shows. Without a selection it takes everything the filter shows, which the server resolves
  // again from the same filter rather than from the rows loaded so far. Either way a file is taken
  // at every path it was reported at.
  const openImport = () =>
    onEditStatus({
      importScope: {
        reportedWindow,
        isSigned,
        search: trimmedSearch || null,
        fileIds: selected.size > 0 ? [...selected] : null,
        fileCount: selected.size > 0 ? selected.size : totalCount,
      },
      onSaved: () => setSelected(new Set()),
    });

  const importButton = (
    <TableActionButton
      variant="outlined"
      onClick={openImport}
      disabled={!canImport || filesQuery.isLoading || (selected.size === 0 && totalCount === 0)}
      sx={{ px: 1.5 }}>
      {selected.size > 0
        ? `Create WDAC Import (${formatCount(selected.size)} File${selected.size === 1 ? '' : 's'})`
        : 'Create WDAC Import (All)'}
    </TableActionButton>
  );

  return (
    <Box sx={{ position: 'relative' }}>
      <ListActionBar
        onSearch={setSearchValue}
        defaultSearch={searchValue}
        onResetSearch={() => setSearchValue('')}
        searchPlaceholder="Search by path, file name, SHA-256 or Authenticode hash..."
        filters={filters}
        onFilterChange={onFilterChange}
        filterOptions={TELEMETRY_FILTER_OPTIONS}
        filterChips
        filterButtonCount={filesQuery.data ? totalCount : undefined}
        actionArea={
          <Stack direction="row" gap={1} alignItems="center">
            <ColumnVisibilityMenu
              columns={WIN32_FILE_COLUMNS}
              isVisible={isColumnVisible}
              onChange={setColumnVisible}
              onReset={resetColumns}
            />
            {canImport ? (
              importButton
            ) : (
              <Tooltip title={IMPORT_DISABLED_REASON}>
                {/* A disabled button fires no events, so the tooltip hangs off a wrapper. */}
                <span>{importButton}</span>
              </Tooltip>
            )}
          </Stack>
        }
        actionAreaContainerProps={{ flexShrink: 0 }}
        contentArea={
          <Stack direction={{ xs: 'column', md: 'row' }} gap={2} sx={{ width: '100%' }}>
            {COVERAGE_CARDS.map(card => {
              const count = countsQuery.data?.[card.countKey] ?? 0;

              return (
                <CoverageCard
                  key={card.label}
                  active={isCovered === card.isCovered}
                  onClick={() => onCoverageCardClick(card.isCovered)}>
                  <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
                    {countsQuery.isLoading ? '-' : formatCount(count)}
                  </Typography>
                  <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2" sx={{ color: '#DDDDDD', fontWeight: 600 }}>
                      {card.label}
                    </Typography>
                    <Iconify icon="mdi:arrow-right" width={16} height={16} color="#9CA3AF" />
                    <Typography variant="body2" sx={{ color: '#BDBDBD' }}>
                      {card.nextStep}
                    </Typography>
                  </Stack>
                </CoverageCard>
              );
            })}
          </Stack>
        }
        contentAreaContainerProps={{ sx: { mt: 1, mb: 1 } }}
      />

      <Win32FileTable
        rows={results}
        columns={visibleColumnDefinitions}
        isLoading={filesQuery.isLoading}
        isFetchingNextPage={filesQuery.isFetchingNextPage}
        lastElementRef={sentryRef}
        searchValue={trimmedSearch}
        sort={sort}
        onSort={sortKey => setSort(previous => toggleFileSort(previous, sortKey))}
        selection={{
          selected,
          enabled: canImport,
          onToggle: toggleSelected,
          onSetSelected: fileIds => setSelected(new Set(fileIds)),
        }}
        onRowClick={row => setOpenFileId(row.fileId)}
        emptyText={
          isCovered === false
            ? 'Every reported file is already in the app.'
            : isCovered === true
              ? 'No reported files are in the app yet.'
              : 'No devices have reported files for this app.'
        }
      />

      <Win32FileDetailModal
        open={!!openFileId}
        onClose={() => setOpenFileId(null)}
        globalApplicationVersionId={globalApplicationVersionId}
        fileId={openFileId}
      />
    </Box>
  );
};

export default TelemetryFilesTable;
